import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import {
  securityVendorService,
  type SecurityVendor,
  type VendorFormInput,
  type SecurityDomain,
  type ServiceRegion,
  type ComplianceCertification,
} from '@/services/securityVendor.service';
import {
  DOMAIN_LABELS,
  REGION_LABELS,
  CERTIFICATION_LABELS,
  TIER_LABELS,
  CONTRACT_STATUS_LABELS,
  REVIEW_CADENCE_LABELS,
  DOMAIN_VALUES,
  REGION_VALUES,
  CERTIFICATION_VALUES,
  TIER_VALUES,
  CONTRACT_STATUS_VALUES,
} from './vendorOptions';

const REVIEW_CADENCE_VALUES = Object.keys(REVIEW_CADENCE_LABELS) as Array<
  keyof typeof REVIEW_CADENCE_LABELS
>;

/** Format an ISO date to `yyyy-mm-dd` for a native date input. */
function toDateInput(value?: string | null): string {
  return value ? new Date(value).toISOString().slice(0, 10) : '';
}

interface VendorFormModalProps {
  /** When provided, the modal edits this vendor; otherwise it nominates a new one. */
  vendor?: SecurityVendor | null;
  onClose: () => void;
  onSaved: (vendor: SecurityVendor) => void;
}

type FormState = {
  name: string;
  description: string;
  websiteUrl: string;
  logoUrl: string;
  vendorTier: string;
  domains: SecurityDomain[];
  regions: ServiceRegion[];
  certifications: ComplianceCertification[];
  countries: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  accountManagerName: string;
  accountManagerEmail: string;
  contractStatus: string;
  contractStartDate: string;
  contractEndDate: string;
  certificationExpiryDate: string;
  reviewCadence: string;
  slaSummary: string;
};

function initialState(vendor?: SecurityVendor | null): FormState {
  return {
    name: vendor?.name ?? '',
    description: vendor?.description ?? '',
    websiteUrl: vendor?.websiteUrl ?? '',
    logoUrl: vendor?.logoUrl ?? '',
    vendorTier: vendor?.vendorTier ?? '',
    domains: vendor?.domains ?? [],
    regions: vendor?.regions ?? [],
    certifications: vendor?.certifications ?? [],
    countries: (vendor?.countries ?? []).join(', '),
    primaryContactName: vendor?.primaryContactName ?? '',
    primaryContactEmail: vendor?.primaryContactEmail ?? '',
    primaryContactPhone: vendor?.primaryContactPhone ?? '',
    accountManagerName: vendor?.accountManagerName ?? '',
    accountManagerEmail: vendor?.accountManagerEmail ?? '',
    contractStatus: vendor?.contractStatus ?? '',
    contractStartDate: toDateInput(vendor?.contractStartDate),
    contractEndDate: toDateInput(vendor?.contractEndDate),
    certificationExpiryDate: toDateInput(vendor?.certificationExpiryDate),
    reviewCadence: vendor?.reviewCadence ?? '',
    slaSummary: vendor?.slaSummary ?? '',
  };
}

/** Toggle a value in/out of an array (checkbox pill groups). */
function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * VendorFormModal — one modal for both quick-add (nominate) and edit (VIS-199).
 * Only `name` is required, so nominating is fast. Lifecycle `status` is NOT editable
 * here (VIS-203) — transitions go through the FSM-driven control on the detail page so
 * an illegal transition can't be sent (L-032). Contract/certification expiry dates and
 * the periodic-review cadence are set here; the backend fires reminders from them.
 */
export default function VendorFormModal({ vendor, onClose, onSaved }: VendorFormModalProps) {
  const isEdit = Boolean(vendor);
  const [form, setForm] = useState<FormState>(() => initialState(vendor));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const buildPayload = (): VendorFormInput => {
    const countries = form.countries
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const payload: VendorFormInput = {
      name: form.name.trim(),
      description: form.description || undefined,
      websiteUrl: form.websiteUrl || undefined,
      logoUrl: form.logoUrl || undefined,
      vendorTier: (form.vendorTier || undefined) as VendorFormInput['vendorTier'],
      domains: form.domains,
      regions: form.regions,
      certifications: form.certifications,
      countries,
      primaryContactName: form.primaryContactName || undefined,
      primaryContactEmail: form.primaryContactEmail || undefined,
      primaryContactPhone: form.primaryContactPhone || undefined,
      accountManagerName: form.accountManagerName || undefined,
      accountManagerEmail: form.accountManagerEmail || undefined,
      contractStatus: (form.contractStatus || undefined) as VendorFormInput['contractStatus'],
      contractStartDate: form.contractStartDate || undefined,
      contractEndDate: form.contractEndDate || undefined,
      certificationExpiryDate: form.certificationExpiryDate || undefined,
      reviewCadence: (form.reviewCadence || undefined) as VendorFormInput['reviewCadence'],
      slaSummary: form.slaSummary || undefined,
    };
    return payload;
  };

  const mutation = useMutation({
    mutationFn: (payload: VendorFormInput) =>
      isEdit && vendor ? securityVendorService.update(vendor.id, payload) : securityVendorService.create(payload),
    onSuccess: (saved) => {
      toast.success(isEdit ? 'Vendor updated' : 'Vendor nominated');
      onSaved(saved);
    },
    // L-019: every mutation surfaces its failure to the user.
    onError: (error: unknown) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save vendor');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    // Inverted contract range is rejected server-side too (the API is the trust boundary);
    // this only spares the user a round trip.
    if (
      form.contractStartDate &&
      form.contractEndDate &&
      form.contractStartDate > form.contractEndDate
    ) {
      toast.error('Contract start date must be on or before the end date');
      return;
    }
    mutation.mutate(buildPayload());
  };

  const pill = (active: boolean) =>
    cn(
      'px-3 py-1 text-xs font-medium rounded-full border transition-colors cursor-pointer select-none',
      active
        ? 'bg-primary-600 text-white border-primary-600'
        : 'bg-transparent text-secondary-600 border-secondary-300 hover:bg-secondary-50 dark:text-secondary-300 dark:border-secondary-600 dark:hover:bg-secondary-800'
    );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit vendor' : 'Nominate vendor'}
    >
      <div className="my-8 w-full max-w-2xl rounded-xl bg-white shadow-xl dark:bg-secondary-900">
        <div className="flex items-center justify-between border-b border-secondary-200 px-6 py-4 dark:border-secondary-700">
          <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">
            {isEdit ? 'Edit Vendor' : 'Nominate a Vendor'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-secondary-500 hover:bg-secondary-100 dark:hover:bg-secondary-800"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="max-h-[70vh] space-y-5 overflow-y-auto px-6 py-5">
          <Input
            label="Vendor name"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. Acme Security Group"
          />

          <div>
            <label htmlFor="vendor-description" className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">
              Description
            </label>
            <textarea
              id="vendor-description"
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Website URL" value={form.websiteUrl} onChange={(e) => set('websiteUrl', e.target.value)} placeholder="https://…" />
            <Input label="Logo URL" value={form.logoUrl} onChange={(e) => set('logoUrl', e.target.value)} placeholder="https://…" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">Tier</label>
              <select
                value={form.vendorTier}
                onChange={(e) => set('vendorTier', e.target.value)}
                className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                <option value="">—</option>
                {TIER_VALUES.map((t) => (
                  <option key={t} value={t}>{TIER_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="vendor-review-cadence" className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                Periodic review cadence
              </label>
              <select
                id="vendor-review-cadence"
                value={form.reviewCadence}
                onChange={(e) => set('reviewCadence', e.target.value)}
                className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                <option value="">—</option>
                {REVIEW_CADENCE_VALUES.map((c) => (
                  <option key={c} value={c}>{REVIEW_CADENCE_LABELS[c]}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-secondary-700 dark:text-secondary-300">Security domains</label>
            <div className="flex flex-wrap gap-2">
              {DOMAIN_VALUES.map((d) => (
                <span key={d} role="button" tabIndex={0} className={pill(form.domains.includes(d))}
                  onClick={() => set('domains', toggle(form.domains, d))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); set('domains', toggle(form.domains, d)); } }}
                >
                  {DOMAIN_LABELS[d]}
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-secondary-700 dark:text-secondary-300">Service regions</label>
            <div className="flex flex-wrap gap-2">
              {REGION_VALUES.map((r) => (
                <span key={r} role="button" tabIndex={0} className={pill(form.regions.includes(r))}
                  onClick={() => set('regions', toggle(form.regions, r))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); set('regions', toggle(form.regions, r)); } }}
                >
                  {REGION_LABELS[r]}
                </span>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-secondary-700 dark:text-secondary-300">Certifications</label>
            <div className="flex flex-wrap gap-2">
              {CERTIFICATION_VALUES.map((c) => (
                <span key={c} role="button" tabIndex={0} className={pill(form.certifications.includes(c))}
                  onClick={() => set('certifications', toggle(form.certifications, c))}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); set('certifications', toggle(form.certifications, c)); } }}
                >
                  {CERTIFICATION_LABELS[c]}
                </span>
              ))}
            </div>
          </div>

          <Input
            label="Countries (comma-separated)"
            value={form.countries}
            onChange={(e) => set('countries', e.target.value)}
            placeholder="United States, United Kingdom"
          />

          <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Primary contact name" value={form.primaryContactName} onChange={(e) => set('primaryContactName', e.target.value)} />
            <Input label="Primary contact email" type="email" value={form.primaryContactEmail} onChange={(e) => set('primaryContactEmail', e.target.value)} />
            <Input label="Primary contact phone" value={form.primaryContactPhone} onChange={(e) => set('primaryContactPhone', e.target.value)} />
            <Input label="Account manager name" value={form.accountManagerName} onChange={(e) => set('accountManagerName', e.target.value)} />
            <Input label="Account manager email" type="email" value={form.accountManagerEmail} onChange={(e) => set('accountManagerEmail', e.target.value)} />
          </fieldset>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">Contract status</label>
              <select
                value={form.contractStatus}
                onChange={(e) => set('contractStatus', e.target.value)}
                className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                <option value="">—</option>
                {CONTRACT_STATUS_VALUES.map((s) => (
                  <option key={s} value={s}>{CONTRACT_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="vendor-contract-start" className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                Contract start date
              </label>
              <input
                id="vendor-contract-start"
                type="date"
                value={form.contractStartDate}
                onChange={(e) => set('contractStartDate', e.target.value)}
                className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="vendor-contract-end" className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                Contract end date
              </label>
              <input
                id="vendor-contract-end"
                type="date"
                value={form.contractEndDate}
                onChange={(e) => set('contractEndDate', e.target.value)}
                className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="vendor-cert-expiry" className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                Certification expiry date
              </label>
              <input
                id="vendor-cert-expiry"
                type="date"
                value={form.certificationExpiryDate}
                onChange={(e) => set('certificationExpiryDate', e.target.value)}
                className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              />
            </div>
          </div>

          <div>
            <label htmlFor="vendor-sla" className="mb-1 block text-sm font-medium text-secondary-700 dark:text-secondary-300">SLA summary</label>
            <textarea
              id="vendor-sla"
              value={form.slaSummary}
              onChange={(e) => set('slaSummary', e.target.value)}
              rows={2}
              className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
            />
          </div>

          <div className="flex justify-end gap-3 border-t border-secondary-200 pt-4 dark:border-secondary-700">
            <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {isEdit ? 'Save changes' : 'Nominate vendor'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
